import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { oltAPI } from '../../services/api';
import { LoadingBlock, EmptyState, SignalBadge, StatusDot, CARD } from './TechnicalUI';
import { ProfileModal, UpgradeModal } from '../../pages/OLTPage';
import { Wifi, Search, Power, PowerOff, RotateCcw, Tag, UploadCloud, Trash2, Router, ChevronRight, ChevronDown } from 'lucide-react';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'online', label: 'Online' },
  { key: 'offline', label: 'Offline' },
];

// OLT reports adminState as "enable" / "disable" — matches OLTPage's isOnuEnabled.
const isOnuEnabled = (o) => (o.adminState || '').toLowerCase() !== 'disable';

// Shared ONU list + hardware actions (profile, firmware, restart, enable/disable, deregister)
// — used both by the full-page ONU List (`variant="page"`) and embedded read-write in the
// Network Overview dashboard (`variant="panel"`, scroll-capped so it doesn't take over the
// screen). One implementation so the two surfaces can never drift apart.
export default function OnuManagerPanel({ variant = 'page', seedQuery = '', maxHeight }) {
  const [onus, setOnus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [ponPort, setPonPort] = useState('');
  const [search, setSearch] = useState(seedQuery);
  const [toggling, setToggling] = useState({});
  const [rebooting, setRebooting] = useState({});
  const [profileModal, setProfileModal] = useState(null);
  const [upgradeModal, setUpgradeModal] = useState(null);
  // Panel variant is a dropdown, closed by default — same "collapse by default, header still
  // carries the summary" rule as LinkHealthCard's Need Attention block. This list is the full
  // read-write ONU manager (search, PON filter, per-row hardware actions); left open it ran
  // the whole home screen off the bottom of the phone.
  const [open, setOpen] = useState(false);

  useEffect(() => {
    oltAPI.getONUs().then(({ data }) => setOnus(data.data || [])).catch(() => setOnus([])).finally(() => setLoading(false));
  }, []);

  const refetch = () => oltAPI.getONUs().then(({ data }) => setOnus(data.data || [])).catch(() => {});

  const handleToggleState = async (o) => {
    const enabled = isOnuEnabled(o);
    const action = enabled ? 'disable' : 'enable';
    const label = enabled ? 'Deactivate' : 'Activate';
    if (!window.confirm(`${label} ONU ${o.label || o.description || o.serialNumber || o.id}?`)) return;
    setToggling(p => ({ ...p, [o.id]: true }));
    try {
      await oltAPI.setONUState(o.slot, o.port, o.onuId, action);
      toast.success(`ONU ${action}d`);
      setOnus(prev => prev.map(x => x.id === o.id ? { ...x, adminState: action } : x));
    } catch (err) {
      toast.error(err.response?.data?.message || `${label} failed`);
    } finally {
      setToggling(p => { const n = { ...p }; delete n[o.id]; return n; });
    }
  };

  const handleReboot = async (o) => {
    if (!window.confirm(`Reboot ONU ${o.label || o.description || o.serialNumber || o.id}?`)) return;
    setRebooting(p => ({ ...p, [o.id]: true }));
    try {
      await oltAPI.rebootONU(o.slot, o.port, o.onuId);
      toast.success('Reboot command sent');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Reboot failed');
    } finally {
      setRebooting(p => { const n = { ...p }; delete n[o.id]; return n; });
    }
  };

  const handleDeregister = async (o) => {
    if (!window.confirm(`Deregister ONU ${o.label || o.description || o.serialNumber || o.id}? This cannot be undone.`)) return;
    try {
      await oltAPI.deregisterONU(o.slot, o.port, o.onuId);
      toast.success('ONU deregistered');
      refetch();
    } catch (err) { toast.error(err.response?.data?.message || 'Deregister failed'); }
  };

  const ponPortOptions = useMemo(() => {
    if (!onus) return [];
    const seen = new Map();
    for (const o of onus) {
      const key = `${o.slot}/${o.port}`;
      if (!seen.has(key)) seen.set(key, { key, slot: o.slot, port: o.port });
    }
    return [...seen.values()].sort((a, b) => a.slot - b.slot || a.port - b.port);
  }, [onus]);

  const filtered = useMemo(() => {
    if (!onus) return [];
    let rows = onus;
    if (filter !== 'all') rows = rows.filter(o => (filter === 'online' ? o.status === 'online' : o.status !== 'online'));
    if (ponPort) rows = rows.filter(o => `${o.slot}/${o.port}` === ponPort);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(o => `${o.id} ${o.label || ''} ${o.description || ''} ${o.serialNumber || ''}`.toLowerCase().includes(q));
    }
    return rows;
  }, [onus, filter, ponPort, search]);

  if (loading) return <LoadingBlock />;
  if (!onus || onus.length === 0) return <EmptyState icon={Wifi} text="No ONUs found — is the OLT reachable?" />;

  const onlineCount = onus.filter(o => o.status === 'online').length;

  const list = (
    <div style={{ maxHeight, overflowY: maxHeight ? 'auto' : undefined }}>
      {filtered.length === 0 && <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-muted)' }}>No ONUs match</div>}
      {filtered.map(o => {
        const enabled = isOnuEnabled(o);
        return (
          <div key={o.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--cb-f1f5f9)' }}>
            <Link
              to={`/technical/onus/${o.slot}/${o.port}/${o.onuId}`}
              style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: 10 }}
            >
              <StatusDot online={o.status === 'online'} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, fontSize: '.88rem' }}>
                    {o.label || o.description || o.id}
                  </span>
                  <span style={{ fontSize: '.68rem', fontWeight: 700, color: o.status === 'online' ? 'var(--c-0ca30c)' : 'var(--c-d03b3b)' }}>
                    {o.status === 'online' ? 'Online' : 'Offline'}
                  </span>
                </div>
                <div style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>PON {o.slot}/{o.port} · ONU {o.onuId}</div>
              </div>
              <SignalBadge dbm={o.rxPower} />
            </Link>
            <div style={{ display: 'flex', gap: 4, marginTop: 8, marginLeft: 18, flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
              <button className="btn btn-secondary btn-sm" title="Change Line/Service Profile" onClick={() => setProfileModal(o)} style={{ padding: '3px 7px' }}>
                <Tag size={11} />
              </button>
              <button className="btn btn-secondary btn-sm" title="Upgrade ONU Firmware" onClick={() => setUpgradeModal(o)} style={{ padding: '3px 7px' }}>
                <UploadCloud size={11} />
              </button>
              <button className="btn btn-warning btn-sm" title="Restart ONU" disabled={!!rebooting[o.id]} onClick={() => handleReboot(o)} style={{ padding: '3px 7px' }}>
                <RotateCcw size={11} />
              </button>
              <button
                className="btn btn-sm"
                title={enabled ? 'Deactivate ONU' : 'Activate ONU'}
                onClick={() => handleToggleState(o)}
                disabled={!!toggling[o.id]}
                style={{
                  padding: '3px 7px',
                  background: enabled ? 'var(--cb-fef3c7)' : 'var(--cb-d1fae5)',
                  color: enabled ? 'var(--c-92400e)' : 'var(--c-065f46)',
                  border: enabled ? '1px solid var(--cb-fcd34d)' : '1px solid var(--cb-6ee7b7)',
                }}
              >
                {enabled ? <PowerOff size={11} /> : <Power size={11} />}
              </button>
              <button className="btn btn-danger btn-sm" title="Deregister ONU" onClick={() => handleDeregister(o)} style={{ padding: '3px 7px' }}>
                <Trash2 size={11} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );

  const controls = (
    <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
      <div style={{ position: 'relative', flex: '1 1 220px' }}>
        <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
        <input className="form-control" style={{ paddingLeft: 32 }} placeholder="Search ONU id, label, serial…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>
      <select className="form-control" style={{ width: 150 }} value={ponPort} onChange={e => setPonPort(e.target.value)}>
        <option value="">All PON Ports</option>
        {ponPortOptions.map(p => (
          <option key={p.key} value={p.key}>PON {p.slot}/{p.port}</option>
        ))}
      </select>
      <div style={{ display: 'flex', gap: 6 }}>
        {FILTERS.map(f => (
          <button
            key={f.key}
            className="btn btn-sm"
            style={{ background: filter === f.key ? 'var(--primary)' : undefined, color: filter === f.key ? 'var(--c-ffffff)' : undefined }}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>
    </div>
  );

  const modals = (
    <>
      {profileModal && <ProfileModal onu={profileModal} onClose={() => setProfileModal(null)} onSuccess={refetch} />}
      {upgradeModal && <UpgradeModal onu={upgradeModal} onClose={() => setUpgradeModal(null)} onSuccess={refetch} />}
    </>
  );

  if (variant === 'panel') {
    return (
      <div style={{ ...CARD, padding: '14px 16px 12px', marginBottom: 20 }}>
        <div
          onClick={() => setOpen(o => !o)}
          style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: open ? 14 : 0, cursor: 'pointer', userSelect: 'none' }}
        >
          <Router size={18} color="var(--primary)" style={{ flexShrink: 0 }} />
          <span style={{ fontWeight: 800, fontSize: '1.02rem', color: 'var(--c-111827)' }}>ONUs</span>
          <span style={{ fontSize: '.78rem', color: 'var(--c-6b7280)' }}>{onlineCount} online · {onus.length - onlineCount} offline</span>
          <Link
            to="/technical/onus"
            onClick={e => e.stopPropagation()}
            style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 2, fontSize: '.8rem', fontWeight: 600, flexShrink: 0 }}
          >
            View All <ChevronRight size={15} />
          </Link>
          <ChevronDown
            size={17} color="var(--c-9ca3af)"
            style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}
          />
        </div>
        {open && (
          <>
            {controls}
            {list}
          </>
        )}
        {modals}
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h2>ONU List</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>{onlineCount} online · {onus.length - onlineCount} offline · {onus.length} total</p>
      </div>
      {controls}
      <div style={{ border: '1px solid var(--cb-e5e7eb)', borderRadius: 10, padding: '4px 18px', background: 'var(--cb-ffffff)' }}>
        {list}
      </div>
      {modals}
    </div>
  );
}
